import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import UpdateSecurityIcon from '../../../assets/icons/UpdateSecurityIcon.png';
import LockIcon from '../../../assets/icons/lockIcon2.svg';
import AnswerIcon from '../../../assets/icons/AnswerIcon.svg';
import OtpIcon from '../../../assets/icons/TwoFactorIcon.png';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../auth/AuthContext';
import AlertService from '../../../services/AlertService';
import QuestionsService from '../../../services/QuestionService';
import UserService from '../../../services/UserService';
import { Skeleton } from 'primereact/skeleton';

const UpdateSecurity = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [showInitialOtp, setShowInitialOtp] = useState(true);
  const [showSecurityForm, setShowSecurityForm] = useState(false);
  const [showSecondOtp, setShowSecondOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [aIds, setAIds] = useState([null, null, null]);
  const [q1Value, setQ1Value] = useState('');
  const [q2Value, setQ2Value] = useState('');
  const [q3Value, setQ3Value] = useState('');
  const [a1Value, setA1Value] = useState('');
  const [a2Value, setA2Value] = useState('');
  const [a3Value, setA3Value] = useState('');

  const [errors, setErrors] = useState({
    q1: false, a1: false,
    q2: false, a2: false,
    q3: false, a3: false,
  });

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [pendingPayload, setPendingPayload] = useState(null);

const questionsAbortRef = useRef(null); 


const loadQuestionsAndAnswers = async () => {
    if (questionsAbortRef.current) {
      questionsAbortRef.current.abort(); 
    }
    const controller = new AbortController();
    questionsAbortRef.current = controller;

    setLoading(true);
    try {
      const qData = await QuestionsService.GetAllQuestions(
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (qData.isSuccess) {
        setQuestions(qData.data);
      } else {
        AlertService.error(qData.errorMessage || "Failed to load questions");
        return;
      }

      const aData = await QuestionsService.GetAllAnswers(
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (aData.isSuccess && aData.data.length >= 3) {
        setAIds([aData.data[0].id, aData.data[1].id, aData.data[2].id]);
        setQ1Value(aData.data[0].questionId || '');
        setA1Value(aData.data[0].answer || '');
        setQ2Value(aData.data[1].questionId || '');
        setA2Value(aData.data[1].answer || '');
        setQ3Value(aData.data[2].questionId || '');
        setA3Value(aData.data[2].answer || '');
      } else if (!aData.isSuccess) {
        AlertService.error(aData.errorMessage || "Failed to load answers");
      }
    } catch (ex) {
      if (ex.name === "AbortError") return; 
      AlertService.error("Error loading questions and answers");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {
      q1: !q1Value,
      a1: !a1Value.trim(),
      q2: !q2Value,
      a2: !a2Value.trim(),
      q3: !q3Value,
      a3: !a3Value.trim(),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };


  const handleOtpChange = (e, idx) => {
    const val = e.target.value;
    if (/^\d?$/.test(val)) {
      const newOtp = [...otp];
      newOtp[idx] = val;
      setOtp(newOtp);
      if (val && idx < 5) {
        const nextInput = document.getElementById(`otp-${idx + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      const prevInput = document.getElementById(`otp-${idx - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d{1,6}$/.test(paste)) {
      const newOtp = paste.split('');
      while (newOtp.length < 6) newOtp.push('');
      setOtp(newOtp);
      const lastIdx = paste.length - 1;
      const inputToFocus = document.getElementById(`otp-${lastIdx}`);
      if (inputToFocus) inputToFocus.focus();
    }
  };


const otpControllerRef = useRef(null);

const handleInitialOtpSubmit = async (e) => {
  e.preventDefault();
  const codeValue = otp.join("");
  if (codeValue.length < 6) {
    AlertService.error("Please enter a 6-digit OTP code.");
    return;
  }

  if (otpControllerRef.current) {
    otpControllerRef.current.abort();
  }
  const controller = new AbortController();
  otpControllerRef.current = controller;

  setOtpLoading(true);

  try {
    const result = await UserService.VerifyQrCode(
      codeValue,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      setShowInitialOtp(false);
      setShowSecurityForm(true);
      setOtp(["", "", "", "", "", ""]);
      await loadQuestionsAndAnswers();
    } else {
      AlertService.error(result.errorMessage || "Invalid OTP code");
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    AlertService.error("Error verifying OTP.");
  } finally {
    if (!controller.signal.aborted) setOtpLoading(false);
  }
};



  const onSubmitHandler = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = [
      { id: aIds[0], questionId: q1Value, answer: a1Value.trim() },
      { id: aIds[1], questionId: q2Value, answer: a2Value.trim() },
      { id: aIds[2], questionId: q3Value, answer: a3Value.trim() },
    ];

    setPendingPayload(payload);
    setOtp(['', '', '', '', '', '']);
    setShowSecurityForm(false);
    setShowSecondOtp(true);
  };

const handleSecondOtpSubmit = async (e) => {
  e.preventDefault();
  const codeValue = otp.join("");
  if (codeValue.length < 6) {
    AlertService.error("Please enter a 6-digit OTP code.");
    return;
  }

  if (otpControllerRef.current) {
    otpControllerRef.current.abort();
  }
  const controller = new AbortController();
  otpControllerRef.current = controller;

  setOtpLoading(true);

  try {
    const result = await UserService.VerifyQrCode(
      codeValue,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      if (!pendingPayload) {
        AlertService.error("No data to save after OTP verification.");
        return;
      }

      setLoading(true);

      const saveResult = await QuestionsService.Save(
        pendingPayload,
        authContext,
        controller.signal
      );

      if (controller.signal.aborted) return;

      if (saveResult.isSuccess) {
        authContext.setAnswered(true);
        AlertService.success("Security answers updated successfully.");
        navigate("/profile");
      } else {
        AlertService.error(
          saveResult.errorMessage || "Failed to save security answers."
        );
      }
    } else {
      AlertService.error(result.errorMessage || "Invalid OTP code");
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    AlertService.error("Error verifying OTP or saving answers.");
  } finally {
    if (!controller.signal.aborted) {
      setOtpLoading(false);
      setLoading(false);
    }
  }
};

useEffect(() => {
  return () => {
    questionsAbortRef.current?.abort();
    otpControllerRef.current?.abort();
  };
}, []);


  return (
    <Fragment>
      <div className="d-flex profile container-fluid mt-3 pt-4">
        {showInitialOtp && (
          <>
            <div className="OtpVerify-left d-none d-xl-block d-flex justify-content-center align-items-center">
              <img
                src={OtpIcon}
                alt="OTP Verification"
                className='img-fluid mx-auto'
              />
            </div>
            <div className="profile-right d-flex flex-column justify-content-center w-100 mx-auto mt-5 pt-5 mt-xl-0 pt-xl-0" style={{ maxWidth: '550px' }}>
              {otpLoading ? (
                <>
                  <center>
                    <Skeleton width="60%" height="3rem" className="mb-4" />
                  </center>
                  <div className="mb-4">
                    <Skeleton width="100%" height="3rem" className="mb-2" />
                    <Skeleton width="100%" height="3rem" />
                  </div>
                  <div className="d-flex justify-content-center mb-4 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <Skeleton key={i} width="3rem" height="3rem" className="rounded" />
                    ))}
                  </div>
                  <div className="d-flex justify-content-between mt-5">
                    <Skeleton width="30%" height="2.5rem" />
                    <Skeleton width="30%" height="2.5rem" />
                  </div>
                </>
              ) : (
                <>
                  <h4 className="mb-4 text-center text-xl-start">Two Factor Verification</h4>
                  <p className="mb-4 text-center text-xl-start">
                    Please provide the One-Time Password code generated by{' '}
                    <strong>Microsoft Authentication</strong> or any{' '}
                    <strong>Authentication</strong> app in the textbox below
                  </p>
                  <Form onSubmit={handleInitialOtpSubmit}>
                    <div className="d-flex justify-content-center mb-4 gap-3" onPaste={handlePaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          id={`otp-${i}`}
                          type="text"
                          maxLength="1"
                          inputMode="numeric"
                          className="form-control text-center w-100 otp-input custom-input-color"
                          value={digit}
                          onChange={e => handleOtpChange(e, i)}
                          onKeyDown={e => handleOtpKeyDown(e, i)}
                          onFocus={e => e.target.select()}
                          aria-label={`OTP digit ${i + 1}`}
                          autoComplete="off"
                          disabled={otpLoading}
                          autoFocus={i === 0} 

                        />
                      ))}
                    </div>
                    <div className="d-flex justify-content-between mt-4 pt-2">
                      <Button
                        variant="outline-secondary"
                        className="fw-bold px-lg-5 py-2"
                        onClick={() => navigate('/profile')}
                        type="button"
                        disabled={otpLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="ms-auto px-lg-5 py-2 d-flex align-items-center justify-content-center"
                        disabled={otpLoading}
                        type="submit"
                      >
                        Verify
                      </Button>
                    </div>
                  </Form>
                </>
              )}
            </div>
          </>
        )}

        {showSecurityForm && (
          <>
            <div className="security-left d-flex justify-content-center align-items-center d-none d-xl-block">
              <img
                src={UpdateSecurityIcon}
                alt="Update Security"
                className='img-fluid w-100 mx-auto' style={{ maxHeight: '85vh' }}
              />
            </div>
            <div className="profile-right d-flex flex-column justify-content-center w-100 mx-auto mt-5 pt-5 mt-xl-0 pt-xl-0" style={{ maxWidth: '550px' }} >

              {loading ? (
                <div className="w-100">

                  <center><h4 className='mb-4'>Update Security Questions</h4></center>
                  <div className="mb-4"><Skeleton width="100%" height="3.5rem" /></div>
                  <div className="mb-2"><Skeleton width="20%" height="2rem" /></div>
                  <div className="mb-4"><Skeleton width="100%" height="3.5rem" /></div>
                  <div className="mb-2"><Skeleton width="25%" height="2rem" /></div>
                  <div className="mb-4"><Skeleton width="100%" height="3.5rem" /></div>
                  <div className="mt-5 d-flex">
                    <Skeleton width="20%" height="2.5rem" />
                    <Skeleton width="20%" height="2.5rem" className="ms-auto" />
                  </div>
                </div>
              ) : (
                <Form onSubmit={onSubmitHandler}>
                  <center><h4 className='mb-4'>Update Security Questions</h4></center>

                  {[1, 2, 3].map(num => {
                    const qValue = [q1Value, q2Value, q3Value][num - 1];
                    const setQValue = [setQ1Value, setQ2Value, setQ3Value][num - 1];
                    const aValue = [a1Value, a2Value, a3Value][num - 1];
                    const setAValue = [setA1Value, setA2Value, setA3Value][num - 1];

                    return (
                      <div className="mb-3" key={num}>
                        <div className="input-group mb-3">
                          <span className="input-group-text bg-transparent custom-title-color">
                            <img src={LockIcon} alt="lock" />
                          </span>
                          <select
                            value={qValue}
                            onChange={e => setQValue(e.target.value)}
                            className={`form-select custom-input border-start-0 rounded-start-0 rounded custom-input-color ${errors[`q${num}`] ? 'is-invalid' : ''}`}
                            aria-label={`Security question ${num}`}
                          >
                            <option value="" disabled>Select question {num}</option>
                            {questions.map(q => (
                              <option key={q.id} value={q.id}>{q.question}</option>
                            ))}
                          </select>
                          <div className="invalid-feedback">Please select a question.</div>
                        </div>

                        <div className="input-group">
                          <span className="input-group-text bg-transparent border-end-0 rounded-end-0 custom-title-color">
                            <img src={AnswerIcon} alt="answer" className="ps-1" />
                          </span>
                          <input
                            type="text"
                            value={aValue}
                            onChange={e => setAValue(e.target.value)}
                            placeholder="Enter your answer"
                            maxLength={30}
                            className={`form-control custom-input border-start-0 rounded-start-0 rounded-end custom-input-color ${errors[`a${num}`] ? 'is-invalid' : ''}`}
                          />
                          <div className="invalid-feedback">Please provide an answer.</div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="d-flex justify-content-between mt-4 pt-2">
                    <Button
                      variant="outline-secondary"
                      className="fw-bold px-lg-5 py-2"
                      onClick={() => navigate('/profile')}
                      type="button"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="ms-auto px-lg-5 py-2 d-flex align-items-center justify-content-center"
                      type="submit"
                      disabled={loading}
                    >
                      {loading && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      )}
                      Submit
                    </Button>
                  </div>
                </Form>
              )}
            </div>
          </>
        )}
        {showSecondOtp && (
          <>
            <div className="OtpVerify-left d-flex justify-content-center align-items-center d-none d-xl-block">
              <img
                src={OtpIcon}
                alt="OTP Verification"
                className='img-fluid'
              />
            </div>
            <div className="profile-right d-flex flex-column justify-content-center w-100 mx-auto mt-5 pt-5 mt-xl-0 pt-xl-0" style={{ maxWidth: '550px' }}>
              {otpLoading ? (
                <>
                  <center>
                    <Skeleton width="60%" height="3rem" className="mb-4" />
                  </center>
                  <div className="mb-4">
                    <Skeleton width="100%" height="3rem" className="mb-2" />
                    <Skeleton width="100%" height="3rem" />
                  </div>
                  <div className="d-flex justify-content-center mb-4 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <Skeleton key={i} width="3rem" height="3rem" className="rounded" />
                    ))}
                  </div>
                  <div className="d-flex justify-content-between mt-5">
                    <Skeleton width="30%" height="3.5rem" />
                    <Skeleton width="30%" height="3.5rem" />
                  </div>
                </>
              ) : (
                <>
                  <h4 className="mb-4 text-center text-xl-start">Two Factor Verification</h4>
                  <p className="mb-4 text-center text-xl-start">
                    Please provide the One-Time Password code generated by{' '}
                    <strong>Microsoft Authentication</strong> or any{' '}
                    <strong>Authentication</strong> app in the textbox below
                  </p>
                  <Form onSubmit={handleSecondOtpSubmit}>
                    <div className="d-flex justify-content-center mb-4 gap-3" onPaste={handlePaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          id={`otp-${i}`}
                          type="text"
                          maxLength="1"
                          inputMode="numeric"
                          className="form-control text-center w-100 otp-input custom-input-color"
                          value={digit}
                          onChange={e => handleOtpChange(e, i)}
                          onKeyDown={e => handleOtpKeyDown(e, i)}
                          onFocus={e => e.target.select()}
                          aria-label={`OTP digit ${i + 1}`}
                          autoComplete="off"
                          disabled={otpLoading}
                          autoFocus={i === 0} 

                        />
                      ))}
                    </div>
                    <div className="d-flex justify-content-between mt-4 pt-2">
                      <Button
                        variant="outline-secondary"
                        className="fw-bold px-lg-5 py-2"
                        onClick={() => {
                          setShowSecondOtp(false);
                          setShowSecurityForm(true);
                          setOtp(['', '', '', '', '', '']);
                        }}
                        disabled={otpLoading}
                        type="button"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="ms-auto px-lg-5 py-2 d-flex align-items-center justify-content-center"
                        disabled={otpLoading}
                        type="submit"
                      >
                        Verify  </Button>
                    </div>
                  </Form>
                </>
              )}
            </div>
          </>
        )}

      </div>
    </Fragment>
  );
};

export default UpdateSecurity;
